import { createSelector } from 'reselect'
import * as sortBy from 'lodash/sortBy'
import { PEER_GROUP_COLORS } from 'constants/peergroups'
import { Guards } from 'interfaces/guards'
import { CategoryId } from 'interfaces/meta'
import { StoreDispatch } from 'interfaces/component'
import { AccountCollection, AggregatedBalanceByCategory } from 'interfaces/accounts'
import { ALL_CATEGORY_ID } from 'constants/meta'

import { creditAccountsToBreakdown } from 'helpers/accountHelpers'
import { formatPercent, roundPercent } from 'helpers/formatHelpers'
import { bindGuard, connectShadedLoadable, createParallelGuard } from 'helpers/loadableHelpers'
import { COLOR_SET } from 'helpers/categoriesHelpers'
import { createEmptyCollection, getItemsByKey, getItemsFromCollection, setItemInCollection } from 'helpers/normalizeHelpers'

import { metaSelector } from 'selectors/globalSelectors'
import { createPeergroupBreakdownSelector } from 'selectors/accountSelectors'
import { creditAccountsSelector, highlightedGenericCategorySelector } from 'selectors/creditSelectors'
import { selectedPeergroupIdSelector, selectedRelationSelector, yourPeergroupIdSelector } from 'selectors/peergroupSelectors'

import { peergroupsGuard } from 'guards/globalGuards'
import { creditAccountsGuard } from 'guards/creditGuards'
import { peergroupAccountsBreakdownGuard } from 'guards/accountGuards'

import { TimeframeTyped } from 'interfaces/global'
import { highlightCategoryActionCreator } from 'actions/globalActions'
import { createForgottenGuardError } from 'helpers/errorHelpers'

import CategoriesModule, {
  CategoriesModuleDispatchProps,
  CategoriesModuleOwnProps,
  CategoriesModuleStateProps,
} from 'components/features/pfm/CategoriesModule/CategoriesModule'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { trackUIEventAction } from 'actions/analyticsActions'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'
import { CreditAccount } from 'interfaces/credit'

export type CreditAccountsModuleOwnProps = CategoriesModuleOwnProps & TimeframeTyped

function fillNegativeBreakdownWithZero (breakdown: AccountCollection): AccountCollection {
  const { allKeys, byKey } = breakdown

  let sum = 0
  const collection = allKeys.reduce((result, categoryId) => {
    if (categoryId === ALL_CATEGORY_ID) {
      return result
    }

    const amount = byKey[categoryId].amount < 0 ? 0 : byKey[categoryId].amount
    sum += amount

    const item: AggregatedBalanceByCategory = {
      ...byKey[categoryId],
      category: categoryId,
      amount,
    }

    return setItemInCollection<AggregatedBalanceByCategory, 'category'>(result, item, 'category')
  }, createEmptyCollection())

  return setItemInCollection(collection, { category: ALL_CATEGORY_ID, amount: sum }, 'category')
}

const creditAccountsOrderSelector = createSelector(
  metaSelector,
  creditAccountsSelector,
  createPeergroupBreakdownSelector('debts', yourPeergroupIdSelector),
  createPeergroupBreakdownSelector('debts', selectedPeergroupIdSelector),
  (meta, creditAccounts, peersBreakdown, othersBreakdown) => {
    if (!creditAccounts || !peersBreakdown || !othersBreakdown) {
      return null
    }

    const categoryIds = meta.debt_categories.allKeys
    const individualCategoryIds = categoryIds.filter((id) => id !== ALL_CATEGORY_ID)
    const creditAccountsArray = getItemsFromCollection<CreditAccount, 'id'>(creditAccounts)
    const filteredCreditAccountsArray = creditAccountsArray.filter(({ balance }) => balance > 0)
    const memberBreakdown = creditAccountsToBreakdown(filteredCreditAccountsArray, categoryIds)
    const iteraties = [memberBreakdown, peersBreakdown, othersBreakdown]
      .map((breakdown): Record<CategoryId, number> => {
        // build relative values map
        const totalAmount = breakdown.byKey[ALL_CATEGORY_ID].amount

        return breakdown.allKeys.reduce((result, categoryId) => {
          const categoryAmount = breakdown.byKey[categoryId].amount
          const relativeAmount = totalAmount === 0 || categoryAmount === 0
            ? 0
            : roundPercent(categoryAmount / totalAmount, 'one')

          return {
            ...result,
            [categoryId]: relativeAmount,
          }
        }, {})
      })
      .map((breakdown) => (id: CategoryId) => breakdown[id] ? -breakdown[id] : 0)

    return sortBy(individualCategoryIds, ...iteraties)
  }
)


const createMapStateToProps = () =>
  createSelector(
    metaSelector,
    creditAccountsOrderSelector,
    creditAccountsSelector,
    createPeergroupBreakdownSelector('debts', yourPeergroupIdSelector),
    createPeergroupBreakdownSelector('debts', selectedPeergroupIdSelector),
    highlightedGenericCategorySelector,
    selectedRelationSelector,
    isMobileSelector,
    withComparisonSelector,
    (meta, order, creditAccounts, peersBreakdown, othersBreakdown, highlightedId, selectedRelation, isMobile, withComparison): CategoriesModuleStateProps => {
      if (!peersBreakdown || !othersBreakdown || !order) {
        throw createForgottenGuardError('CreditAccountsModuleContainer', 'peergroupAccountsBreakdownGuard')
      }

      if (!creditAccounts) {
        throw createForgottenGuardError('CreditAccountsModuleContainer', 'creditAccountsGuard')
      }

      if (!selectedRelation) {
        throw createForgottenGuardError('CreditAccountsModuleContainer', 'peergroupsGuard')
      }

      const categories = meta.debt_categories.byKey
      const categoryIds = meta.debt_categories.allKeys

      // accounts with negative values are discarded
      const creditAccountsArray = getItemsFromCollection<CreditAccount, 'id'>(creditAccounts)
      const filteredCreditAccountsArray = creditAccountsArray.filter(({ balance }) => balance > 0)
      const memberBreakdown = creditAccountsToBreakdown(filteredCreditAccountsArray, categoryIds)

      const buildPie = (breakdown: AccountCollection, colorSet: string[], fontColorSet: string[], fontColor: string) => {
        const { [ALL_CATEGORY_ID]: accountsTotal, ...restItems } = fillNegativeBreakdownWithZero(breakdown).byKey

        const ordered = getItemsByKey(restItems, order)
        const isFilled = ordered.some((item) => item.amount !== 0)

        const chart = isFilled
          ? ordered.map(({ category: id, amount }, idx) => {
            const ratio = amount / accountsTotal.amount

            return {
              id,
              value: Math.round(ratio * 1000),
              title: categories[id].value,
              subtitle: formatPercent(ratio, 'one'),
              color: colorSet[idx],
              fontColor: fontColorSet[idx],
              tooltipFontColor: fontColor,
              isHighlighted: highlightedId === id,
              isSelected: false,
              isShaded: highlightedId !== id && highlightedId !== null && highlightedId !== ALL_CATEGORY_ID,
            }
          })
          : []

        return {
          mainColor: colorSet[0],
          mainFontColor: fontColorSet[0],
          total: accountsTotal.amount,
          chart,
        }
      }

      return {
        withList: false,
        isMobile,
        selectedPeergroupName: selectedRelation.name,
        member: buildPie(
          memberBreakdown,
          COLOR_SET.member,
          COLOR_SET.memberText,
          PEER_GROUP_COLORS.member
        ),
        peers: buildPie(
          peersBreakdown,
          COLOR_SET.peers,
          COLOR_SET.peersText,
          PEER_GROUP_COLORS.peers
        ),
        others: buildPie(
          othersBreakdown,
          COLOR_SET.others,
          COLOR_SET.othersText,
          PEER_GROUP_COLORS.others
        ),
        withComparison,
      }
    }
  )

const mapDispatchToProps = (dispatch: StoreDispatch): CategoriesModuleDispatchProps => ({
  highlightCategory: (category) => dispatch(highlightCategoryActionCreator('creditscore', category)),
  trackUIEvent: (event) => dispatch(trackUIEventAction(event)),
})

const guards: Guards<CreditAccountsModuleOwnProps> = [
  peergroupsGuard,
  createParallelGuard(
    bindGuard(peergroupAccountsBreakdownGuard, { type: 'debts' }),
    creditAccountsGuard,
  ),
]

const CreditAccountsModuleContainer = connectShadedLoadable<
  CategoriesModuleStateProps,
  CategoriesModuleDispatchProps,
  CategoriesModuleOwnProps,
  CreditAccountsModuleOwnProps
>(createMapStateToProps, mapDispatchToProps, guards)(CategoriesModule)
export default CreditAccountsModuleContainer
